import { CalendarSync, Download, Upload, Unlink, ExternalLink, RefreshCw } from "lucide-react";

export const GoogleSync = ({ status, accent, busy, onConnect, onPull, onPush, onAuto, onDisconnect }) => (
  <div className="mt-5 rounded-2xl border border-white/10 bg-panel p-4" data-testid="google-sync">
    <div className="flex flex-wrap items-center gap-3">
      <p className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest" style={{ color: accent }}>
        <CalendarSync className="h-3.5 w-3.5" /> Google Calendar
      </p>
      {status.connected && (
        <span className="text-xs text-ink-soft" data-testid="google-account">{status.email}</span>
      )}
    </div>

    {!status.configured && (
      <div className="mt-2 space-y-1.5 text-xs text-ink-soft" data-testid="google-setup">
        <p>
          To connect Google I need your own Google OAuth credentials — Google issues them per project, so nobody
          else's will do.
        </p>
        <p>
          In the{" "}
          <a
            href="https://console.cloud.google.com/apis/credentials"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 underline decoration-dotted hover:text-ink"
          >
            Google Cloud console <ExternalLink className="h-3 w-3" />
          </a>
          : enable the <span className="text-ink">Google Calendar API</span>, then create an{" "}
          <span className="text-ink">OAuth client ID → Web application</span> with this redirect URI:
        </p>
        <code className="block break-all rounded-lg border border-white/10 bg-page px-2.5 py-1.5 text-[11px] text-ink">
          {window.location.origin}/api/oauth/calendar/callback
        </code>
        <p>Send me the Client ID and Client Secret in the chat and I'll switch this on.</p>
      </div>
    )}

    {status.configured && !status.connected && (
      <div className="mt-2 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-xs text-ink-soft">
            Connect your Google account and your work meetings will sit beside your practice days.
          </p>
          <button
            data-testid="google-connect"
            onClick={onConnect}
            disabled={busy}
            className="ms-auto min-h-[36px] rounded-full px-5 text-xs font-semibold text-page active:scale-95"
            style={{ backgroundColor: accent }}
          >
            {busy ? "Opening Google…" : "Connect Google Calendar"}
          </button>
        </div>
        <p className="text-[11px] text-ink-soft">
          If Google answers <span className="text-ink">Error 403: access_denied</span>, your app is still in
          Testing: open{" "}
          <a
            href="https://console.cloud.google.com/auth/audience"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 underline decoration-dotted hover:text-ink"
          >
            Google Auth Platform → Audience <ExternalLink className="h-3 w-3" />
          </a>{" "}
          and add your own Google address under <span className="text-ink">Test users</span>. This OAuth client must
          also list this exact redirect URI:
        </p>
        <code className="block break-all rounded-lg border border-white/10 bg-page px-2.5 py-1.5 text-[11px] text-ink"
              data-testid="google-redirect-uri">
          {window.location.origin}/api/oauth/calendar/callback
        </code>
      </div>
    )}

    {status.connected && (
      <>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button
            data-testid="google-pull"
            onClick={onPull}
            disabled={busy}
            className="inline-flex min-h-[36px] items-center gap-1.5 rounded-full border border-white/10 px-4 text-xs font-semibold text-ink-soft transition-colors hover:text-ink"
          >
            <Download className="h-3.5 w-3.5" /> Bring Google events in
          </button>
          <button
            data-testid="google-push"
            onClick={onPush}
            disabled={busy}
            className="inline-flex min-h-[36px] items-center gap-1.5 rounded-full border border-white/10 px-4 text-xs font-semibold text-ink-soft transition-colors hover:text-ink"
          >
            <Upload className="h-3.5 w-3.5" /> Send mine up to Google
          </button>
          <button
            data-testid="google-disconnect"
            onClick={onDisconnect}
            className="ms-auto inline-flex min-h-[36px] items-center gap-1.5 rounded-full border border-white/10 px-4 text-xs font-semibold text-ink-soft transition-colors hover:border-destructive/60 hover:text-destructive"
          >
            <Unlink className="h-3.5 w-3.5" /> Disconnect
          </button>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-white/5 pt-2">
          <span className="inline-flex items-center gap-1.5 text-xs text-ink-soft">
            <RefreshCw className="h-3.5 w-3.5" /> Keep in step every hour
          </span>
          <button
            data-testid="google-auto-toggle"
            onClick={() => onAuto(!status.auto_sync)}
            aria-pressed={status.auto_sync}
            className="rounded-full border px-3.5 py-1.5 text-xs font-semibold"
            style={status.auto_sync
              ? { borderColor: accent, color: accent, backgroundColor: `${accent}22` }
              : { borderColor: "rgba(255,255,255,.12)", color: "#9AA3B2" }}
          >
            {status.auto_sync ? "On" : "Off"}
          </button>
          {status.last_synced && (
            <span className="text-xs text-ink-soft" data-testid="google-last-synced">
              Last checked {new Date(status.last_synced).toLocaleString()}
            </span>
          )}
        </div>
      </>
    )}
  </div>
);
